import { Component, computed, EventEmitter, inject, OnInit, Output, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  iHasCaptionLeft,
  IHasCaptionLeft,
  iHasInputColorControls,
  IHasInputColorControls,
  InteractiveItemComponent
} from '../../common-items/InteractiveItem/interactive-item-form.component';
import {
  DEFAULT_INTERACTIVE_PROPERTIES,
  InteractiveItemProperties
} from '../../common-items/InteractiveItem/interactive-item-properties';
import { IBaseItem } from '../../common-items/BaseItem/base-item-form.component';

import { getPropsFrom, upsertChildren, xmlElementToString } from '../../../../utils/xmlParser';
import { DEFAULT_INITIAL_DATE } from '../../../../utils/dates';
import { LiveFormsTranslateLoaderService } from '../../../services/live-forms-translate-loader.service';
import { MatIcon } from '@angular/material/icon';
import {
  extractColorProperties,
  getColors,
  getInputStyle,
  getTextColorsPropertiesStyle
} from '../../../../utils/colors';
import { getOptimalFirstColumnWidth } from '../../../../utils/getOptimalWidth';

interface TimeInputProperties {
  text: string;
  style: string;
  selectedDateTime: string;
  captionWidth: number;
  inputType: string;
}

@Component({
  selector: 'lfe-time-input-item-form',
  imports: [CommonModule, FormsModule, ReactiveFormsModule, MatIcon],
  templateUrl: './time-input-item-form.component.html',
  standalone: true,
  styleUrl: './time-input-item-form.component.css'
})
export class TimeInputItemFormComponent
  extends iHasInputColorControls(iHasCaptionLeft(InteractiveItemComponent<TimeInputProperties>))
  implements OnInit, IBaseItem<TimeInputProperties>, IHasCaptionLeft, IHasInputColorControls
{
  translateService = inject(LiveFormsTranslateLoaderService);
  formControl: FormControl<string | null> | undefined;
  properties: InteractiveItemProperties & TimeInputProperties = {
    ...DEFAULT_INTERACTIVE_PROPERTIES,
    text: '',
    style: '',
    selectedDateTime: '',
    captionWidth: 0,
    inputType: 'time'
  };
  disabled = false;

  @Output() updateFirstColumnWidthRequired = new EventEmitter<{ id: string; width: number }>();
  firstColumnWidth = computed(
    () => this.sharedFirstColumnWidths()[this.firstSiblingItemId()] || getOptimalFirstColumnWidth(this.properties.text)
  );

  currentId = computed(() => this.properties.customId || this.properties.id);
  htmlId = computed(() => `time_input_${this.formControlId}`);

  currentTextColor = signal('');

  inputStyle = computed(() => getInputStyle(this.currentInputBgColor(), this.currentInputTextColor()));

  getCaptionStyle(): { [key: string]: string } | null {
    if (!this.properties.text) {
      return null;
    }

    const textStyle = getTextColorsPropertiesStyle(this.properties.textBgColor, this.currentTextColor());

    return {
      width: `${this.firstColumnWidth()}px`,
      overflow: 'hidden',
      'text-overflow': 'ellipsis',
      'white-space': 'nowrap',
      display: 'inline-block',
      ...textStyle
    };
  }

  getCaptionTitle(): string | null {
    if (!this.properties.text) {
      return null;
    }
    if (this.firstColumnWidth() <= 0) {
      return null;
    }
    return this.properties.text;
  }

  override ngOnInit() {
    super.ngOnInit();

    this.formControlId = this.properties.customId || this.properties.id;

    const initialValue = this.parseStringToTimeInput(this.properties.selectedDateTime);
    this.formControl = new FormControl(initialValue);
    this.form().addControl(this.formControlId, this.formControl);

    this.updateFirstColumnWidthRequired.emit({
      id: this.firstSiblingItemId(),
      width: this.firstColumnWidth()
    });
  }

  extractLocalProperties(xml: Element): InteractiveItemProperties & TimeInputProperties {
    const props = getPropsFrom(xml);

    const customId = props['CustomID'];
    const id = props['ID'];
    const text = props['Text'] || '';
    const style = props['Style'] || '';
    const selectedDateTime = props['SelectedDateTime'] || '';
    const captionWidth = Number(props['CaptionWidth'] || 0);
    const inputType = props['Type'] || 'time';
    const activationRule = props['ActivationRule'] || '';
    const isGenericRepeatItem = props['IsGenericRepeatItem'] === 'True';

    const { textBgColor, textColor } = getColors(extractColorProperties(props));

    this.currentTextColor.set(textColor);

    const inputColor = this.initInputColorProperties(props);

    const resultValue = props['ResultValue'] || '';
    const resultUser = props['ResultUser'] || '';
    const resultTime = props['ResultTime'] || '';

    return {
      ...DEFAULT_INTERACTIVE_PROPERTIES,
      id,
      customId,
      activationRule,
      resultValue,
      resultUser,
      resultTime,
      text,
      textBgColor,
      inputColor,
      style,
      selectedDateTime,
      captionWidth,
      inputType,
      isGenericRepeatItem
    };
  }

  private parseStringToTimeInput(value: string): string | null {
    if (!value || value === DEFAULT_INITIAL_DATE) {
      return null;
    }

    const date = new Date(value);
    if (isNaN(date.getTime())) {
      return null;
    }

    if (date.getUTCFullYear() === 1000 && date.getUTCMonth() === 0 && date.getUTCDate() === 1) {
      return null;
    }

    return this.formatTimeForInput(date);
  }

  private formatTimeForInput(date: Date): string {
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    const seconds = date.getSeconds().toString().padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  }

  private formatTimeForStorage(inputValue: string): string {
    if (!inputValue) return DEFAULT_INITIAL_DATE;

    const [hours, minutes, seconds] = inputValue.split(':');
    const date = new Date();
    date.setUTCFullYear(1000, 0, 1);
    date.setUTCHours(parseInt(hours) || 0, parseInt(minutes) || 0, parseInt(seconds) || 0, 0);

    return date.toISOString();
  }

  onSetNowClick(event: Event): void {
    event.preventDefault();
    const now = new Date();
    const timeString = this.formatTimeForInput(now);
    this.formControl?.setValue(timeString);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.selectedDateTime = this.formatTimeForStorage(timeString);

    this.properties.resultValue = this.properties.selectedDateTime;
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
  }

  onClearTimeClick(event: Event): void {
    event.preventDefault();
    this.formControl?.setValue(null);
    this.formControl?.markAsDirty();
    this.formControl?.markAsTouched();

    this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;

    this.properties.resultValue = this.properties.selectedDateTime;
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
  }

  onTimeChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    const value = input.value;

    if (value) {
      this.properties.selectedDateTime = this.formatTimeForStorage(value);
    } else {
      this.properties.selectedDateTime = DEFAULT_INITIAL_DATE;
    }

    this.properties.resultValue = this.properties.selectedDateTime;
    this.properties.resultUser = this.user().displayName;
    this.properties.resultTime = new Date().toISOString();
  }

  override getXML(): string {
    const element = this.getXmlCloneElement();

    this.handleInputColorStorage(element);

    if (this.properties.selectedDateTime !== DEFAULT_INITIAL_DATE) {
      upsertChildren(element, { SelectedDateTime: this.properties.selectedDateTime });
      this.setResultChildren(element);
    }

    return xmlElementToString(element) || '';
  }
}
